import React from 'react';
import { Box, Typography, Button, Select, MenuItem, Skeleton } from '@mui/material';
import { format } from 'date-fns';
import { CommandIndexProvidersQuery } from '~/graphql/__generated__/graphql';
import { CreateOverrideVisitProvider } from './overrideVisitTypes';
import { useZonedDateFormat } from '~/providers/ZonedDateFormatProvider';

interface PatientProviderFirstStepProps {
  providers: CommandIndexProvidersQuery['commandIndexProviders']['providers'];
  loading: boolean;
  calendarDateTime: Date;
  onProviderSelected: (provider: CreateOverrideVisitProvider) => void;
  onCancel: () => void;
}

const PatientProviderFirstStep: React.FC<PatientProviderFirstStepProps> = ({
  providers,
  loading,
  calendarDateTime,
  onProviderSelected,
  onCancel,
}) => {
  const { timeLabelWithZone, timezone } = useZonedDateFormat();
  const [selectedProviderId, setSelectedProviderId] = React.useState<number | ''>('');

  const formattedDateTime = (calendarDateTime && !isNaN(calendarDateTime.getTime()) 
    ? `${format(calendarDateTime, 'PP')} at ${timeLabelWithZone(calendarDateTime, true, timezone)}`
    : '');

  const activeProviders = React.useMemo(() => providers.filter(p => p.active), [providers]);

  const handleProviderChange = React.useCallback((providerId: number | '') => {
    setSelectedProviderId(providerId);
    if (providerId === '') return;

    const provider = activeProviders.find(p => p.id === providerId);
    if (!provider) return;

    onProviderSelected({
      id: provider.id,
      firstName: provider.firstName || '',
      lastName: provider.lastName || '',
      providerType: provider.providerType ? {
        id: provider.providerType.id,
        name: provider.providerType.name || '',
      } : null,
    });
  }, [activeProviders, onProviderSelected]);

  return (
    <Box sx={{ minWidth: 500 }}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" component="h2" sx={{ mb: 3 }}>
          Create Override Visit{formattedDateTime && ` for ${formattedDateTime}`}
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="caption" color="text.secondary" component="label" htmlFor="patient-override-provider-select">
            Provider
          </Typography>
          {loading ? (
            <Skeleton variant="rectangular" height={40} />
          ) : (
            <Select
              id="patient-override-provider-select"
              data-testid="patient-override-provider-select"
              value={selectedProviderId}
              onChange={(e) => handleProviderChange(e.target.value as number | '')}
              size="small"
              fullWidth
              displayEmpty
            >
              <MenuItem value="" disabled>
                <em>Select a provider</em>
              </MenuItem>
              {activeProviders.map((provider) => (
                <MenuItem key={provider.id} value={provider.id}>
                  {provider.firstName} {provider.lastName}
                  {provider.providerType?.name && (
                    <Typography component="span" sx={{ ml: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
                      ({provider.providerType.name})
                    </Typography>
                  )}
                </MenuItem>
              ))}
            </Select>
          )}
        </Box>
      </Box>

      <Box sx={{ 
        p: 2, 
        borderTop: '1px solid', 
        borderTopColor: 'divider',
        display: 'flex',
        justifyContent: 'flex-end'
      }}>
        <Button onClick={onCancel}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default PatientProviderFirstStep;
